import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSettings } from "../../lib/settings";
import { fileUrl } from "../../lib/api";
import { X, Search, Loader2, Check, User } from "lucide-react";

/** Scrollable, searchable list of actors. onSelect(actor) fires on click. */
export function ActorSearchList({ actors, onSelect, emptyText = "No matching actors.", testidPrefix = "actor" }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return !s ? actors : actors.filter((a) => (a.full_name || "").toLowerCase().includes(s));
  }, [actors, q]);
  return (
    <div>
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
        <input data-testid={`${testidPrefix}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actors…"
          className="w-full bg-[#050505] border border-[#262626] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
      </div>
      <div className="max-h-72 overflow-y-auto border border-[#1a1a1a] divide-y divide-[#1a1a1a]">
        {filtered.length === 0 && <div className="px-3 py-6 text-center text-sm text-[#6B6862]">{emptyText}</div>}
        {filtered.map((a) => (
          <button key={a.id} type="button" onClick={() => onSelect(a)} data-testid={`${testidPrefix}-option-${a.id}`}
            className="w-full text-left px-3 py-2 text-sm hover:bg-blood/10 hover:text-blood transition-colors flex items-center gap-3">
            {a.headshot_file_id ? (
              <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt={a.full_name} loading="lazy" decoding="async" className="w-9 h-9 object-cover border border-[#262626] shrink-0 bg-[#1a1a1a]" />
            ) : (
              <span className="w-9 h-9 bg-[#1a1a1a] border border-[#262626] flex items-center justify-center shrink-0"><User size={16} className="text-[#6B6862]" /></span>
            )}
            <span className="flex-1 min-w-0 truncate">{a.full_name}</span>
            <span className="text-[10px] font-mono text-[#6B6862] shrink-0">{(a.available_dates || []).length} dates</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Generic centered modal shell. Portaled to <body> so it always centers in the
 *  viewport, unaffected by ancestor transforms (e.g. page fade-in animations). */
export function ModalShell({ title, subtitle, onClose, children, footer, testid }) {
  return createPortal(
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50" data-testid={testid}>
      <div className="w-full max-w-lg border border-[#1a1a1a] bg-[#0A0A0A] max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#1a1a1a]">
          <div className="min-w-0">
            <h2 className="font-display text-xl uppercase tracking-tight">{title}</h2>
            {subtitle && <p className="text-xs text-[#A19D94] mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="modal-close" className="text-[#6B6862] hover:text-[#F2EBE1] shrink-0"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
        {footer && <div className="p-5 border-t border-[#1a1a1a] flex items-center justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/**
 * Date-based assignment modal.
 * actor: { id, full_name, available_dates }
 * role:  { id, name }
 * showDates: all open dates
 * takenDateIds: dates already assigned to ANOTHER actor for this role (greyed out)
 * occupiedByOther: { show_date_id: "Role name" } — dates the actor is already on for a
 *   DIFFERENT role (Update 27 Change 01); greyed out, can't be in two places at once.
 * onConfirm({ date_ids, send_email })
 */
export function AssignDatesModal({ actor, role, positions = null, showDates, takenDateIds = [], occupiedByOther = {}, onConfirm, onClose }) {
  const { formatDate } = useSettings();
  const available = actor.available_dates || [];
  const [roleId, setRoleId] = useState(role.id);
  const [selected, setSelected] = useState(new Set());
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  const multi = positions && positions.length > 1;
  const effRole = (multi ? positions.find((p) => p.id === roleId) : null) || role;
  const taken = multi
    ? new Set((effRole.assignments || []).map((a) => a.show_date_id))
    : new Set(takenDateIds);

  const selectableIds = showDates.filter((sd) => available.includes(sd.id) && !taken.has(sd.id) && !occupiedByOther[sd.id]).map((sd) => sd.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const confirm = async (preliminary) => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await onConfirm({ date_ids: Array.from(selected), send_email: !preliminary, preliminary, role_id: roleId });
    } finally { setSaving(false); }
  };

  return (
    <ModalShell
      testid="assign-dates-modal"
      title={`Assign ${actor.full_name}`}
      subtitle={`to ${role.name}`}
      onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835]">Cancel</button>
        <button onClick={() => confirm(true)} disabled={selected.size === 0 || saving} data-testid="assign-preliminary-confirm"
          className="px-4 py-2 text-xs uppercase tracking-wider border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 disabled:opacity-40 flex items-center gap-2">
          {saving && <Loader2 size={13} className="animate-spin" />}
          Add as Preliminary
        </button>
        <button onClick={() => confirm(false)} disabled={selected.size === 0 || saving} data-testid="assign-dates-confirm"
          className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 flex items-center gap-2">
          {saving && <Loader2 size={13} className="animate-spin" />}
          Assign &amp; Invite ({selected.size})
        </button>
      </>}
    >
      {multi && (
        <div className="mb-4" data-testid="assign-position-select">
          <label className="text-xs uppercase tracking-wider text-[#A19D94] block mb-1.5">Which {role.name} position?</label>
          <select value={roleId} onChange={(e) => { setRoleId(e.target.value); setSelected(new Set()); }}
            className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood">
            {positions.map((p, i) => {
              const filled = (p.assignments || []).length;
              return <option key={p.id} value={p.id}>{role.name} #{i + 1}{filled ? ` — ${filled} date${filled === 1 ? "" : "s"} filled` : " — empty"}</option>;
            })}
          </select>
        </div>
      )}
      {selectableIds.length > 0 && (
        <label className="flex items-center gap-2 mb-3 cursor-pointer" data-testid="assign-select-all">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-blood" />
          <span className="text-xs uppercase tracking-wider text-[#A19D94]">Select all available dates ({selectableIds.length})</span>
        </label>
      )}
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {showDates.map((sd) => {
          const isAvail = available.includes(sd.id);
          const isTaken = taken.has(sd.id);
          const otherRole = occupiedByOther[sd.id];
          const disabled = !isAvail || isTaken || !!otherRole;
          const checked = selected.has(sd.id);
          return (
            <label key={sd.id} data-testid={`assign-date-${sd.id}`}
              className={`flex items-center gap-3 px-3 py-2.5 border text-sm ${disabled ? "border-[#1a1a1a] bg-[#070707] opacity-50 cursor-not-allowed" : checked ? "border-blood bg-blood/10 cursor-pointer" : "border-[#262626] hover:border-[#3a3835] cursor-pointer"}`}>
              <input type="checkbox" disabled={disabled} checked={checked} onChange={() => toggle(sd.id)} className="accent-blood" />
              <div className="flex-1 min-w-0">
                <div className="text-[#F2EBE1]">{formatDate(sd.date)}</div>
                {sd.name && <div className="text-[10px] text-[#6B6862]">{sd.name}</div>}
              </div>
              {otherRole ? <span className="text-[10px] font-mono uppercase text-[#0EA5E9] text-right shrink-0" data-testid={`assign-date-occupied-${sd.id}`}>Already assigned to {otherRole}</span>
                : !isAvail ? <span className="text-[10px] font-mono uppercase text-[#6B6862]">Not available</span>
                : isTaken ? <span className="text-[10px] font-mono uppercase text-[#F59E0B]" data-testid={`assign-date-filled-${sd.id}`}>Already filled</span>
                : null}
            </label>
          );
        })}
      </div>
      <p className="text-[11px] text-[#6B6862] mt-4 pt-4 border-t border-[#1a1a1a]" data-testid="assign-help">
        <span className="text-[#F59E0B]">Preliminary</span> pencils them in with no email (invite later once your roster is set). <span className="text-blood">Assign &amp; Invite</span> emails the role offer straight away.
      </p>
    </ModalShell>
  );
}
